import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import wsfeConfigService, {
  AfipRescateReconciliarItem,
} from '../../services/wsfeConfigService';
import SearchableSelect from '../../components/SearchableSelect';
import {
  facturaPosRescatePath,
  type AfipRescatePosPayload,
  type FacturaPosLocationState,
} from '../../config/routes';
import { importesCoinciden, parseImporteAfip } from '../../utils/arcaRescateComparacion';

type Props = {
  idCompania: string;
  idwsfe: string;
  cuit: string;
  puntoVenta: string;
  onPuntoVentaChange: (v: string) => void;
  onCuitChange: (v: string) => void;
  getCodigoComprobanteAfip: () => number | null;
  tipoSelect: string;
  tipoOtro: string;
  onTipoSelectChange: (v: string) => void;
  onTipoOtroChange: (v: string) => void;
  tiposComprobanteOptions: { value: string; label: string }[];
};

const estadoChip = (estado: string) => {
  switch (estado) {
    case 'Ok':
      return <Chip size="small" label="OK" color="success" />;
    case 'LocalSinCae':
      return <Chip size="small" label="Sin CAE local" color="warning" />;
    case 'HuerfanaArca':
      return <Chip size="small" label="Huérfana ARCA" color="error" />;
    case 'CaeDistinto':
      return <Chip size="small" label="CAE distinto" color="warning" />;
    case 'NoEnArca':
      return <Chip size="small" label="No en ARCA" variant="outlined" />;
    default:
      return <Chip size="small" label={estado || 'Error'} color="default" />;
  }
};

function formatComprobanteFiscal(letra: string | null | undefined, nroSuc: number | string, nroComp: number): string {
  const letraFmt = (letra ?? '').trim() || '?';
  const suc = String(Number(nroSuc) || 0).padStart(4, '0');
  const nro = String(nroComp || 0).padStart(8, '0');
  return `${letraFmt}-${suc}-${nro}`;
}

function formatImporte(valor?: string | number | null): string {
  const n = parseImporteAfip(valor);
  if (n == null) {
    const v = valor == null ? '' : String(valor).trim();
    return v || '—';
  }
  return n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatFechaAfip(fecha?: string | null): string {
  const f = (fecha ?? '').trim();
  if (!f) return '—';
  if (f.length === 8 && /^\d+$/.test(f)) {
    return `${f.slice(6, 8)}/${f.slice(4, 6)}/${f.slice(0, 4)}`;
  }
  const d = new Date(f);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleDateString('es-AR');
  }
  return f;
}

function formatFechaLocal(fecha?: string | null): string {
  if (!fecha) return '—';
  const d = new Date(fecha);
  if (Number.isNaN(d.getTime())) return fecha;
  return d.toLocaleDateString('es-AR');
}

function formatCuit(value?: string | null): string {
  const limpio = (value ?? '').replace(/\D/g, '');
  if (!limpio) return '—';
  if (limpio.length === 11) {
    return `${limpio.slice(0, 2)}-${limpio.slice(2, 10)}-${limpio.slice(10)}`;
  }
  return value?.trim() || limpio;
}

function valoresCoinciden(a?: string | number | null, b?: string | number | null): boolean | null {
  const na = normalizarComparable(a);
  const nb = normalizarComparable(b);
  if (na === '' && nb === '') return null;
  if (na === '' || nb === '') return null;
  return na === nb;
}

function normalizarComparable(v?: string | number | null): string {
  if (v == null || v === '') return '';
  if (typeof v === 'number') return v.toFixed(2);
  const s = String(v).trim();
  if (!s) return '';
  // fechas yyyyMMdd
  if (/^\d{8}$/.test(s)) return s;
  // ISO date → yyyyMMdd
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    return s.slice(0, 10).replace(/-/g, '');
  }
  const digits = s.replace(/\D/g, '');
  if (digits.length >= 8 && /^\d/.test(s.replace(/[.\s]/g, ''))) {
    // CUIT / doc
    if (digits.length === 11 || digits.length === 8) return digits;
  }
  const n = Number(s.replace(',', '.'));
  if (!Number.isNaN(n) && /[\d.,]/.test(s) && !/[a-zA-Z]/.test(s)) {
    return n.toFixed(2);
  }
  return s.toLowerCase();
}

type ComparacionFila = {
  campo: string;
  arca: string;
  local: string;
  coincide: boolean | null;
};

function buildComparacion(row: AfipRescateReconciliarItem): ComparacionFila[] {
  const d = row.detalleArca;
  const filas: ComparacionFila[] = [
    {
      campo: 'Comprobante',
      arca: [
        d?.puntoVenta != null ? `Pto ${d.puntoVenta}` : null,
        d?.codigoComprobante != null ? `Tipo ${d.codigoComprobante}` : null,
        `Nro ${row.numeroComprobante}`,
      ]
        .filter(Boolean)
        .join(' · ') || `Nro ${row.numeroComprobante}`,
      local:
        row.nroFormularioLocal != null
          ? [
              row.idCompaniaLocal ? `Cia ${row.idCompaniaLocal}` : null,
              row.letraLocal ? `Letra ${row.letraLocal}` : null,
              row.nroSucLocal != null ? `Suc ${row.nroSucLocal}` : null,
              row.nroComprobanteLocal != null ? `Nro ${row.nroComprobanteLocal}` : null,
              `#${row.nroFormularioLocal}`,
            ]
              .filter(Boolean)
              .join(' · ')
          : '— (ausente)',
      coincide: row.nroFormularioLocal != null ? true : null,
    },
    {
      campo: 'CAE',
      arca: row.caeArca || d?.cae || '—',
      local: row.caeLocal || '—',
      coincide: valoresCoinciden(row.caeArca || d?.cae, row.caeLocal),
    },
    {
      campo: 'Vencimiento CAE',
      arca: formatFechaAfip(row.fechaVencimientoCae || d?.fechaVencimientoCae),
      local: formatFechaLocal(row.caeVencimientoLocal),
      coincide: valoresCoinciden(row.fechaVencimientoCae || d?.fechaVencimientoCae, row.caeVencimientoLocal),
    },
    {
      campo: 'Fecha comprobante',
      arca: formatFechaAfip(row.fechaComprobanteArca || d?.fechaComprobante),
      local: formatFechaLocal(row.fechaEmisionLocal),
      coincide: valoresCoinciden(row.fechaComprobanteArca || d?.fechaComprobante, row.fechaEmisionLocal),
    },
    {
      campo: 'Documento / CUIT',
      arca: formatCuit(row.docNroArca || d?.docNro),
      local: formatCuit(row.cuitLocal),
      coincide: valoresCoinciden(row.docNroArca || d?.docNro, row.cuitLocal),
    },
    {
      campo: 'Cliente local',
      arca: '—',
      local:
        row.nroClienteLocal != null
          ? `${row.nroClienteLocal}${row.nombreLocal ? ` — ${row.nombreLocal}` : ''}`
          : '—',
      coincide: null,
    },
    {
      campo: 'Importe neto',
      arca: `$${formatImporte(row.impNetoArca ?? d?.impNeto)}`,
      local: row.impNetoLocal != null ? `$${formatImporte(row.impNetoLocal)}` : '—',
      coincide: importesCoinciden(row.impNetoArca ?? d?.impNeto, row.impNetoLocal),
    },
    {
      campo: 'IVA',
      arca: `$${formatImporte(row.impIvaArca ?? d?.impIva)}`,
      local: row.impIvaLocal != null ? `$${formatImporte(row.impIvaLocal)}` : '—',
      coincide: importesCoinciden(row.impIvaArca ?? d?.impIva, row.impIvaLocal),
    },
    {
      campo: 'Importe total',
      arca: `$${formatImporte(row.importeTotalArca ?? d?.importeTotal)}`,
      local: row.importeTotalLocal != null ? `$${formatImporte(row.importeTotalLocal)}` : '—',
      coincide: importesCoinciden(row.importeTotalArca ?? d?.importeTotal, row.importeTotalLocal),
    },
  ];

  if (d?.impOpEx && Number(String(d.impOpEx).replace(',', '.')) !== 0) {
    filas.push({
      campo: 'Importe exento',
      arca: `$${formatImporte(d.impOpEx)}`,
      local: '—',
      coincide: null,
    });
  }
  if (d?.impTrib && Number(String(d.impTrib).replace(',', '.')) !== 0) {
    filas.push({
      campo: 'Tributos',
      arca: `$${formatImporte(d.impTrib)}`,
      local: '—',
      coincide: null,
    });
  }
  if (d?.resultado || row.caeResultadoLocal) {
    filas.push({
      campo: 'Resultado AFIP',
      arca: d?.resultado || '—',
      local: row.caeResultadoLocal || '—',
      coincide: valoresCoinciden(d?.resultado, row.caeResultadoLocal),
    });
  }
  if (d?.observaciones) {
    filas.push({
      campo: 'Observaciones ARCA',
      arca: d.observaciones,
      local: '—',
      coincide: null,
    });
  }

  return filas;
}

const CompaniaArcaRescateTab: React.FC<Props> = ({
  idCompania,
  idwsfe,
  cuit,
  puntoVenta,
  onPuntoVentaChange,
  onCuitChange,
  getCodigoComprobanteAfip,
  tipoSelect,
  tipoOtro,
  onTipoSelectChange,
  onTipoOtroChange,
  tiposComprobanteOptions,
}) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [nroDesde, setNroDesde] = useState('');
  const [nroHasta, setNroHasta] = useState('');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<AfipRescateReconciliarItem[]>([]);
  const [letra, setLetra] = useState<string | null>(null);
  const [nroTipoFormulario, setNroTipoFormulario] = useState<string | null>(null);
  const [aplicandoNro, setAplicandoNro] = useState<number | null>(null);

  const [detalleOpen, setDetalleOpen] = useState(false);
  const [detalleItem, setDetalleItem] = useState<AfipRescateReconciliarItem | null>(null);

  const handleReconciliar = async () => {
    const tc = getCodigoComprobanteAfip();
    const pv = parseInt(puntoVenta, 10);
    const desde = parseInt(nroDesde, 10);
    const hasta = parseInt(nroHasta, 10);
    if (!cuit.trim()) {
      enqueueSnackbar('CUIT requerido', { variant: 'warning' });
      return;
    }
    if (!tc || Number.isNaN(pv) || pv < 1) {
      enqueueSnackbar('Punto de venta y tipo de comprobante inválidos', { variant: 'warning' });
      return;
    }
    if (Number.isNaN(desde) || Number.isNaN(hasta) || desde < 1 || hasta < desde) {
      enqueueSnackbar('Indique un rango de números válido (desde ≤ hasta)', { variant: 'warning' });
      return;
    }
    if (hasta - desde + 1 > 50) {
      enqueueSnackbar('El rango máximo es 50 comprobantes', { variant: 'warning' });
      return;
    }

    setLoading(true);
    setItems([]);
    setLetra(null);
    setNroTipoFormulario(null);
    try {
      const result = await wsfeConfigService.rescateReconciliar(idwsfe, {
        idCompania,
        cuit: cuit.trim(),
        puntoVenta: pv,
        codigoComprobanteAfip: tc,
        numeroDesde: desde,
        numeroHasta: hasta,
      });
      setItems(result.items ?? []);
      setLetra(result.letra ?? null);
      setNroTipoFormulario(result.nroTipoFormulario ?? null);
      enqueueSnackbar(`Reconciliación: ${(result.items ?? []).length} comprobante(s)`, { variant: 'info' });
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || e?.message || 'Error al reconciliar', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAplicarCae = async (row: AfipRescateReconciliarItem) => {
    const tc = getCodigoComprobanteAfip();
    const pv = parseInt(puntoVenta, 10);
    if (!tc || Number.isNaN(pv)) return;
    setAplicandoNro(row.numeroComprobante);
    try {
      const result = await wsfeConfigService.rescateAplicarCae(idwsfe, {
        idCompania,
        cuit: cuit.trim(),
        puntoVenta: pv,
        codigoComprobanteAfip: tc,
        numeroComprobante: row.numeroComprobante,
        nroFormulario: row.nroFormularioLocal ?? undefined,
      });
      enqueueSnackbar(result.mensaje || 'CAE aplicado', { variant: 'success' });
      await handleReconciliar();
    } catch (e: any) {
      const msg =
        e?.response?.data?.mensaje ||
        e?.response?.data?.message ||
        e?.message ||
        'Error al aplicar CAE';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setAplicandoNro(null);
    }
  };

  const irARescatePos = (row: AfipRescateReconciliarItem) => {
    const tc = getCodigoComprobanteAfip();
    const pv = parseInt(puntoVenta, 10);
    if (!tc || Number.isNaN(pv) || pv < 1) {
      enqueueSnackbar('Punto de venta y tipo de comprobante inválidos', { variant: 'warning' });
      return;
    }

    navigate(facturaPosRescatePath(), {
      state: {
        afipRescate: {
          idWsfe: idwsfe,
          idCompania,
          cuitEmisor: cuit.trim(),
          puntoVenta: pv,
          codigoComprobanteAfip: tc,
          numeroComprobante: row.numeroComprobante,
          letra: letra ?? undefined,
          cae: row.caeArca || row.detalleArca?.cae,
          fechaComprobante: row.fechaComprobanteArca || row.detalleArca?.fechaComprobante,
          fechaVencimientoCae: row.fechaVencimientoCae || row.detalleArca?.fechaVencimientoCae,
          importeTotal: row.importeTotalArca ?? row.detalleArca?.importeTotal,
          impNeto: row.impNetoArca ?? row.detalleArca?.impNeto,
          impIva: row.impIvaArca ?? row.detalleArca?.impIva,
          docNro: row.docNroArca || row.detalleArca?.docNro,
          docTipo: row.docTipoArca ?? row.detalleArca?.docTipo ?? null,
          returnTo: `/sistema/companias/${encodeURIComponent(idCompania)}`,
        } satisfies AfipRescatePosPayload,
      } satisfies FacturaPosLocationState,
    });
  };

  const abrirDetalle = (row: AfipRescateReconciliarItem) => {
    setDetalleItem(row);
    setDetalleOpen(true);
  };

  const detalleComprobanteLabel = detalleItem
    ? formatComprobanteFiscal(letra, puntoVenta, detalleItem.numeroComprobante)
    : '';

  return (
    <Box sx={{ py: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Rescate de comprobantes ARCA
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Compara un rango de números con ARCA (máx. 50). Permite aplicar CAE a facturas locales sin CAE o
        rescatar huérfanas en el POS de facturación.
      </Typography>

      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} md={8}>
          <Stack spacing={2}>
            <TextField
              label="C.U.I.T. emisor"
              value={cuit}
              onChange={(e) => onCuitChange(e.target.value)}
              fullWidth
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <SearchableSelect
              label="Tipo comprobante AFIP"
              value={tipoSelect || null}
              onChange={(val) => onTipoSelectChange(val ?? '1')}
              options={tiposComprobanteOptions}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            {tipoSelect === 'otro' && (
              <TextField
                label="Código AFIP"
                value={tipoOtro}
                onChange={(e) => onTipoOtroChange(e.target.value)}
                fullWidth
                size="small"
                type="number"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Punto de venta"
                value={puntoVenta}
                onChange={(e) => onPuntoVentaChange(e.target.value)}
                fullWidth
                size="small"
                type="number"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                label="Nro. desde"
                value={nroDesde}
                onChange={(e) => setNroDesde(e.target.value)}
                fullWidth
                size="small"
                type="number"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                label="Nro. hasta"
                value={nroHasta}
                onChange={(e) => setNroHasta(e.target.value)}
                fullWidth
                size="small"
                type="number"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Stack>
          </Stack>
        </Grid>
        <Grid item xs={12} md={4}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            disabled={loading}
            onClick={() => void handleReconciliar()}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
            sx={{ textTransform: 'none', borderRadius: 2, py: 1.5 }}
          >
            Reconciliar con ARCA
          </Button>
          {letra && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Letra mapeada: {letra}
            </Typography>
          )}
        </Grid>
      </Grid>

      {items.length > 0 && (
        <TableContainer sx={{ mt: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nro</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>CAE ARCA</TableCell>
                <TableCell>Total ARCA</TableCell>
                <TableCell>Local</TableCell>
                <TableCell>Mensaje</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row) => (
                <TableRow key={row.numeroComprobante}>
                  <TableCell>{row.numeroComprobante}</TableCell>
                  <TableCell>{estadoChip(row.estado)}</TableCell>
                  <TableCell>{row.caeArca || '—'}</TableCell>
                  <TableCell>${formatImporte(row.importeTotalArca)}</TableCell>
                  <TableCell>
                    {row.nroFormularioLocal != null
                      ? `#${row.nroFormularioLocal}${row.caeLocal ? ` / CAE ${row.caeLocal}` : ''}`
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{row.mensaje}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                      <Tooltip title="Ver detalle ARCA vs eOne">
                        <IconButton size="small" color="primary" onClick={() => abrirDetalle(row)}>
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {row.estado === 'LocalSinCae' && (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={aplicandoNro === row.numeroComprobante}
                          onClick={() => void handleAplicarCae(row)}
                          sx={{ textTransform: 'none' }}
                        >
                          {aplicandoNro === row.numeroComprobante ? '…' : 'Aplicar CAE'}
                        </Button>
                      )}
                      {row.estado === 'HuerfanaArca' && nroTipoFormulario === '1' && (
                        <Button
                          size="small"
                          variant="contained"
                          color="warning"
                          onClick={() => irARescatePos(row)}
                          sx={{ textTransform: 'none' }}
                        >
                          Rescatar en POS
                        </Button>
                      )}
                      {row.estado === 'HuerfanaArca' && nroTipoFormulario !== '1' && (
                        <Typography variant="caption" color="text.secondary">
                          Solo FC
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={detalleOpen} onClose={() => setDetalleOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: '#1976d2' }}>
          Detalle ARCA vs eOne —{' '}
          {detalleComprobanteLabel || `Nro. ${detalleItem?.numeroComprobante ?? ''}`}
        </DialogTitle>
        <DialogContent>
          {detalleItem && (
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                {estadoChip(detalleItem.estado)}
                <Typography variant="body2" color="text.secondary">
                  {detalleItem.mensaje}
                </Typography>
              </Stack>

              <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Campo</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>ARCA</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>eOne (local)</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} width={100} align="center">
                        Match
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {buildComparacion(detalleItem).map((f) => (
                      <TableRow
                        key={f.campo}
                        sx={{
                          bgcolor:
                            f.coincide === false
                              ? 'rgba(211, 47, 47, 0.06)'
                              : f.coincide === true
                                ? 'rgba(46, 125, 50, 0.04)'
                                : undefined,
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {f.campo}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {f.arca}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {f.local}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {f.coincide === true && <Chip size="small" label="OK" color="success" />}
                          {f.coincide === false && <Chip size="small" label="Diff" color="error" />}
                          {f.coincide === null && (
                            <Typography variant="caption" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {detalleItem.detalleArca && (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Datos adicionales ARCA
                  </Typography>
                  <Typography variant="body2">
                    Concepto: {detalleItem.detalleArca.concepto ?? '—'}
                    {detalleItem.detalleArca.monId
                      ? ` · Moneda: ${detalleItem.detalleArca.monId}${
                          detalleItem.detalleArca.monCotiz
                            ? ` (cotiz. ${detalleItem.detalleArca.monCotiz})`
                            : ''
                        }`
                      : ''}
                    {detalleItem.detalleArca.condicionIvaReceptorId != null
                      ? ` · Cond. IVA receptor: ${detalleItem.detalleArca.condicionIvaReceptorId}`
                      : ''}
                    {detalleItem.detalleArca.fchProceso
                      ? ` · Proceso: ${formatFechaAfip(detalleItem.detalleArca.fchProceso)}`
                      : ''}
                  </Typography>
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {detalleItem?.estado === 'LocalSinCae' && (
            <Button
              variant="outlined"
              disabled={aplicandoNro === detalleItem.numeroComprobante}
              onClick={() => {
                void handleAplicarCae(detalleItem);
                setDetalleOpen(false);
              }}
              sx={{ textTransform: 'none', mr: 'auto' }}
            >
              Aplicar CAE
            </Button>
          )}
          {detalleItem?.estado === 'HuerfanaArca' && nroTipoFormulario === '1' && (
            <Button
              variant="contained"
              color="warning"
              onClick={() => {
                setDetalleOpen(false);
                irARescatePos(detalleItem);
              }}
              sx={{ textTransform: 'none', mr: 'auto' }}
            >
              Rescatar en POS
            </Button>
          )}
          <Button onClick={() => setDetalleOpen(false)} sx={{ textTransform: 'none' }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompaniaArcaRescateTab;
